
import { Vue, Component, Prop } from 'vue-property-decorator';
import { Getter, Action } from 'vuex-class';
import { shuffle } from 'lodash';
import CartBanners from './CartBanners.vue';
import CartItemList from './CartItemList.vue';
import CartOrderList from './CartOrderList.vue';
import CartUpsellItemList from './CartUpsellItemList.vue';
import CartPricing from './CartPricing.vue';
import CartSuiteTab from './CartSuiteTab.vue';
import BannerContent from '@/components/shared/BannerContent.vue';
import Modal from '@/components/shared/Modal.vue';
import MenuItemViewer from '@/components/menu/MenuItemViewer.vue';
import WarningModal from '@/components/shared/WarningModal.vue';
import AvailableDiscountsBanner from '@/components/shared/AvailableDiscountsBanner.vue';
import MembershipProgramForm from '@/components/navigation/MembershipProgramForm.vue';

const namespace: string = 'cart';

@Component<CartViewer>({
	components: {
		BannerContent,
		CartBanners,
		CartItemList,
		CartOrderList,
		CartPricing,
		CartUpsellItemList,
		CartSuiteTab,
		MembershipProgramForm,
		Modal,
		MenuItemViewer,
		AvailableDiscountsBanner,
		WarningModal
	}
})
export default class CartViewer extends Vue {
	@Prop({ type: Boolean, required: true, default: false }) private displayChangePreOrderButton!: boolean;
	@Action('validateCart', { namespace }) private validateCart!: () => void;
	@Action('setLegal', { namespace }) private setLegal!: (value: boolean) => void;
	@Getter('getItems', { namespace }) private items!: OrderItem[];
	@Getter('getRotationalMenuOrders', { namespace }) private rotationalMenuOrders!: RotationalMenuOrder[];
	@Getter('getTaxInfo', { namespace }) private taxInfo!: TaxInfo[];
	@Getter('getSubtotal', { namespace }) private subtotal!: string;
	@Getter('getSubsidyDiscountAmount', { namespace }) private subsidyDiscountAmount!: string;
	@Getter('getPromoDiscount', { namespace }) private promoDiscount!: string;
	@Getter('getMemberDiscount', { namespace }) private memberDiscount!: string;
	@Getter('getServiceCharge', { namespace }) private serviceCharge!: string | null;
	@Getter('getDeliveryCharge', { namespace }) private deliveryCharge!: string | null;
	@Getter('getTaxes', { namespace }) private taxes!: string;
	@Getter('getVoucherAmount', { namespace }) private voucherAmount!: string;
	@Getter('getTotal', { namespace }) private total!: string;
	@Getter('getTotalWithoutTaxes', { namespace }) private totalWithoutTaxes!: string | undefined;
	@Getter('getTip', { namespace }) private tip!: string;
	@Getter('getTotalWithTip', { namespace }) private totalWithTip!: string;
	@Getter('getCreditsApplied', { namespace }) private creditsApplied!: CreditsCosts;
	@Getter('getLegalCheck', { namespace }) private legalChecked!: boolean;
	@Getter('getWarning', { namespace }) private warning!: CostsWarning[] | null;
	@Getter('isGenericCatering', { namespace }) private isGenericCatering!: boolean;
	@Getter('isEventDayOrdering', { namespace: 'suites' }) private isEventDayOrdering!: boolean;
	@Getter('isPreOrdering', { namespace: 'suites' }) private isPreOrdering!: boolean;
	@Getter('isLoginAs', { namespace: 'auth' }) private isLoginAs!: boolean;
	@Getter('getFullName', { namespace: 'auth' }) private fullName!: string;
	@Getter('isK12Location', { namespace: 'restaurant' }) private isK12Location!: boolean;
	@Getter('getFilteredMenus', { namespace: 'restaurant' }) private filteredMenus!: Menu[];
	@Getter('getMaxItemsPerCart', { namespace: 'restaurant' }) private maxItemsPerCart!: number;
	@Getter('isMarketplaceHub', { namespace: 'restaurant' }) private isMarketplaceHub!: boolean;
	@Getter('showDiscountsBanner', { namespace }) private showDiscountsBanner!: boolean;
	@Getter('membershipAppliedSuccessfully', { namespace }) private membershipAppliedSuccessfully!: boolean;
	@Getter('isMembershipProgramOn', { namespace: 'restaurant' }) private isMembershipProgramOn!: string[] | string;
	@Getter('getRestaurantMembershipCustomization', { namespace: 'restaurant' }) private membershipCustomization!: MembershipCustomization;

	$refs!: { cartPricing: CartPricing };

	private showItemDetails: boolean = false;
	private legalCheckRequired: boolean = false;
	private upsellItemId: number|null = null;
	private upsellItemSectionId: number|null|undefined = null;
	private legalCheckedHandler: Function | null = null;
	private warningModalProps: WarningModalProps = {
		show: false,
		title: '',
		message: '',
		confirmButtonText: this.$t('menu.error_validation_dialog_buttons')[0],
		cancelButtonText: '',
		type: 'warning',
		hideCloseButton: false,
		showCheckbox: false,
		checkboxLabel: '',
		onClose: () => this.resetWarningModal(),
		onCancel: () => this.resetWarningModal(),
		onConfirm: () => this.closeCartErrorModal()
	};

	/**
	 * Logged in as banner properties
	 *
	 * @return {BannerItem}
	 */
	private get loggedInAsBanner(): BannerItem {
		return {
			name: 'login-as',
			type: 'info',
			show: this.isLoginAs,
			message: this.$t('menu.logged_in_as_banner'),
			action: {
				text : this.fullName,
				onClick : () => this.$router.push({ path: '/profile', query: this.$route.query, hash: this.$route.hash }).catch(() => {})
			}
		};
	}

	/**
	 * Show the membership form when membership is available but not yet applied
	 *
	 * @return {boolean}
	 */
	private get showCartMembershipForm(): boolean {
		return !!(Array.isArray(this.isMembershipProgramOn) && this.isMembershipProgramOn.includes('Punchh') && this.membershipCustomization && !this.membershipAppliedSuccessfully);
	}

	/**
	 * Check if any item in the cart has price_on_request set
	 *
	 * @return {boolean}
	 */
	private get hasPriceOnRequestItems(): boolean {
		if (!this.isGenericCatering) {
			return false;
		}
		return this.items.some(item => item.price_on_request);
	}

	/**
	 * Check if the cart is full
	 *
	 * @return {boolean}
	 */
	private get isCartFull(): boolean {
		if (!this.maxItemsPerCart) {
			return false;
		}
		const itemsInCart: number = this.items.reduce((sum: number, item: MenuItem) => sum += item.quantity, 0);
		return itemsInCart >= this.maxItemsPerCart;
	}

	/**
	 * Check through each menu and section items to find the upsell items,
	 * and filter out the items that are already in the cart.
	 *
	 * @return {MenuItem[]}
	 */
	private get upsellItems(): MenuItem[] {
		const allUpsellItems = this.filteredMenus.flatMap(menu => {
			const upsellMenuItems = menu.items.filter(item => item.upsell_item && !item.sold_out && !item.unavailable);
			const upsellSectionItems = menu.sections.flatMap(section => section.items.filter(item => item.upsell_item && !item.sold_out && !item.unavailable));

			return [...upsellMenuItems, ...upsellSectionItems];
		});

		const filteredUpsellItems = allUpsellItems.filter(item => !this.items.find((cartItem: MenuItem) => cartItem.id === item.id));

		if (filteredUpsellItems.length <= 3) {
			return filteredUpsellItems;
		}

		return shuffle(filteredUpsellItems).slice(0, 3) as MenuItem[];
	}

	/**
	 * Go to the checkout view
	 *
	 * @return {void}
	 */
	private showCheckout(): void {
		this.$emit('checkout');
	}

	/**
	 * Send event to the parent
	 *
	 * @return {void}
	 */
	private closeModal(): void {
		this.$emit('close');
	}

	/**
	 * Validate the cart with the API to check many caveats
	 * (IE: schedule, different menus availability, sold_out, etc)
	 *
	 * @return {Promise<void>}
	 */
	private async validate(): Promise<void> {
		try {
			await this.validateCart();
			this.showCheckout();
		}
		catch (error) {
			this.openErrorModal(error);
		}
		finally {
			if (!this.warningModalProps.show) {
				this.closeModal();
			}
		}
	}

	private openErrorModal(error: any): void {
		this.warningModalProps.show = true;
		this.warningModalProps.type = 'error';
		this.warningModalProps.message = error.response?.data?.message || this.$t('menu.item_viewer.error_adding_item_to_cart');
	}

	/**
	 * Close the validation error modal.
	 * If the legal check was required and the user has checked it, add the item to the cart.
	 *
	 * @return {void}
	 */
	private closeCartErrorModal(): void {
		if (this.legalCheckRequired && this.legalChecked && this.legalCheckedHandler) {
			this.legalCheckedHandler();
		}
		this.legalCheckRequired = false;
		this.legalCheckedHandler = null;
		this.resetWarningModal();
	}

	/**
	 * Reset the warning modal properties to default values
	 *
	 * @return {void}
	 */
	private resetWarningModal(): void {
		this.warningModalProps = {
			show: false,
			title: '',
			message: '',
			confirmButtonText: this.$t('menu.error_validation_dialog_buttons')[0],
			cancelButtonText: '',
			type: 'warning',
			hideCloseButton: false,
			showCheckbox: false,
			checkboxLabel: '',
			onClose: () => this.warningModalProps.show = false,
			onCancel: () => this.warningModalProps.show = false,
			onConfirm: () => this.closeCartErrorModal()
		};
	}

	/**
	 * Show the upsell item details modal
	 *
	 * @param {number} id - The item id
	 * @return {void}
	 */
	private showItem(itemIds: { id: number, sectionId: number | undefined }): void {
		this.showItemDetails = true;
		this.upsellItemId = itemIds.id;
		this.upsellItemSectionId = itemIds.sectionId;
	}

	/**
	 * Open the legal check modal
	 *
	 * @param {Function} onLegalCheckedHandler - Function to trigger if legal gets checked
	 * @return {void}
	 */
	private openLegalCheckModal(onLegalCheckedHandler: Function): void {
		this.legalCheckRequired = true;
		this.legalCheckedHandler = onLegalCheckedHandler;
		this.warningModalProps.show = true;
		this.warningModalProps.message = this.$t('menu.item_viewer.alcohol_legal_warning');
		this.warningModalProps.hideCloseButton = true;
		this.warningModalProps.showCheckbox = true;
		this.warningModalProps.checkboxLabel = this.$t('utils.error_validation_dialog.legal_age_checkbox');
	}

	/**
	 * Update the cart costs after an upsell item is added to the cart.
	 *
	 * @return {void}
	 */
	private updateCosts(): void {
		const cartPricing = this.$refs.cartPricing;
		cartPricing.getCosts?.();
	}
}
